import dayjs from 'dayjs';
import { useMemo } from 'react';
import { DividendEventsSelectPaginate } from '@/modules/issuer-dashboard/components/DividendEventsSelectPaginate.tsx';
import { CommunicationFormValues } from '@/modules/issuer-dashboard/helpers/buildCommunicationPayload.ts';
import { getAustraliaDisabledDate } from '@/modules/issuer-dashboard/helpers/disableDateRange.ts';
import { getTaxReportYearOptions } from '@/modules/issuer-dashboard/helpers/taxReportYears.ts';
import {
  CalendarInput,
  CalendarInputProps,
  Select,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
} from 'ui';
import { BulkReportType, ReportFormat } from 'api';

const bulkReportTypeOptions: SelectOption<BulkReportType>[] = [
  { value: BulkReportType.Transaction, label: 'Transaction' },
  { value: BulkReportType.HoldingStatement, label: 'Holding statement' },
  { value: BulkReportType.Valuation, label: 'Valuation' },
  { value: BulkReportType.DividendStatement, label: 'Dividend statement' },
  { value: BulkReportType.InvestorDistribution, label: 'Investor distribution' },
  { value: BulkReportType.TaxReturn, label: 'Tax return' },
];

const reportFormatOptions: SelectOption<ReportFormat>[] = [
  { value: ReportFormat.Pdf, label: 'PDF' },
  { value: ReportFormat.Xlsx, label: 'XLSX' },
];

const classReportTypes = [
  BulkReportType.Transaction,
  BulkReportType.HoldingStatement,
  BulkReportType.DividendStatement,
];

const oneEndDateReportTypes = [BulkReportType.Valuation];
const financialYearReportTypes = [BulkReportType.TaxReturn];
const dividendEventReportTypes = [BulkReportType.InvestorDistribution];
export const onlyPdfReportTypes = [
  BulkReportType.DividendStatement,
  BulkReportType.InvestorDistribution,
];

interface IssuerCommunicationBulkReportConfigProps {
  values: CommunicationFormValues;
  registerId: number;
  selectedBulkReportClasses: SelectPaginateOption<number>[];
  selectedDividendEvent: SelectPaginateOption<number> | null;
  onChangeReportType: (value: BulkReportType) => void;
  onChangeClasses: (items: SelectPaginateOption<number>[]) => void;
  onChangeDateRange: CalendarInputProps['rangeOnChange'];
  onChangeEndDate: CalendarInputProps['onChange'];
  onChangeFinancialYear: (value: number | null) => void;
  onChangeDividendEvent: (value: SelectPaginateOption<number> | null) => void;
  onChangeFileType: (value: ReportFormat) => void;
  onAsyncClasses: (page: number, search?: string) => Promise<SelectPaginateOption<number>[]>;
}

export const IssuerCommunicationBulkReportConfig = ({
  values,
  registerId,
  selectedBulkReportClasses,
  selectedDividendEvent,
  onChangeReportType,
  onChangeClasses,
  onChangeDateRange,
  onChangeEndDate,
  onChangeFinancialYear,
  onChangeDividendEvent,
  onChangeFileType,
  onAsyncClasses,
}: IssuerCommunicationBulkReportConfigProps) => {
  const taxReportYearOptions = useMemo(() => getTaxReportYearOptions(11), []);
  const reportType = values.bulk_report_config.report_type;
  const showClass = classReportTypes.includes(reportType);
  const showFinalDate = oneEndDateReportTypes.includes(reportType);
  const showFinancialYear = financialYearReportTypes.includes(reportType);
  const showDividendEvent = dividendEventReportTypes.includes(reportType);
  const showTimeframe = !showFinalDate && !showFinancialYear && !showDividendEvent;
  const reportFormatOptionsFiltered = onlyPdfReportTypes.includes(reportType)
    ? reportFormatOptions.filter(item => item.value !== ReportFormat.Xlsx)
    : reportFormatOptions;

  return (
    <div className="space-y-4">
      <Select
        label="REPORT TYPE"
        options={bulkReportTypeOptions}
        value={values.bulk_report_config.report_type}
        onChange={onChangeReportType}
        className="z-[3]"
      />
      {showClass && (
        <SelectPaginate<number, true>
          isMulti
          label="CLASS"
          cacheUniqs={[registerId]}
          debounceTimeoutSearch={700}
          onAsync={onAsyncClasses}
          value={selectedBulkReportClasses}
          onChange={onChangeClasses}
          className="z-[3]"
        />
      )}
      {showFinalDate && (
        <CalendarInput
          label="FINAL DATE"
          disabledDate={getAustraliaDisabledDate}
          onChange={onChangeEndDate}
          value={
            values.bulk_report_config.end_date ? dayjs(values.bulk_report_config.end_date) : null
          }
        />
      )}
      {showTimeframe && (
        <CalendarInput
          range
          label="TIMEFRAME"
          disabledDate={getAustraliaDisabledDate}
          rangeOnChange={onChangeDateRange}
          rangeValue={
            values.bulk_report_config.start_date && values.bulk_report_config.end_date
              ? [
                  dayjs(values.bulk_report_config.start_date),
                  dayjs(values.bulk_report_config.end_date),
                ]
              : null
          }
          allowEmpty={[true, true]}
        />
      )}
      {showFinancialYear && (
        <Select<number>
          label="Financial year"
          options={taxReportYearOptions}
          value={values.bulk_report_config.financial_year || undefined}
          onChange={onChangeFinancialYear}
          className="z-[3]"
        />
      )}
      {showDividendEvent && (
        <DividendEventsSelectPaginate
          value={selectedDividendEvent}
          onChange={onChangeDividendEvent}
        />
      )}
      <Select
        label="FILE TYPE"
        options={reportFormatOptionsFiltered}
        value={values.bulk_report_config.file_type}
        onChange={onChangeFileType}
        className="z-[2]"
      />
    </div>
  );
};

export const isGeneratedReportConfigComplete = (values: CommunicationFormValues) => {
  const reportType = values.bulk_report_config.report_type;
  const showClass = classReportTypes.includes(reportType);
  const showFinalDate = oneEndDateReportTypes.includes(reportType);
  const showFinancialYear = financialYearReportTypes.includes(reportType);
  const showDividendEvent = dividendEventReportTypes.includes(reportType);
  const showTimeframe = !showFinalDate && !showFinancialYear && !showDividendEvent;

  return !(
    (showClass && !values.bulk_report_config.register_class_ids?.length) ||
    (showFinalDate && !values.bulk_report_config.end_date) ||
    (showTimeframe &&
      (!values.bulk_report_config.start_date || !values.bulk_report_config.end_date)) ||
    (showFinancialYear && !values.bulk_report_config.financial_year) ||
    (showDividendEvent && !values.bulk_report_config.dividend_event_id)
  );
};
